import { A11yModule } from '@angular/cdk/a11y';
import { Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MAT_MENU_PANEL, MatMenu, MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';

import { ColorPaletteComponent } from '@demo/angular/demo-theme/color-palette/color-palette.component';
import { Language, LanguageNames } from '@models/system/language';
import { UserProfileHeaderComponent } from '@module-instance/user-profile/user-profile-header/user-profile-header.component';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { CipoImgBase64 } from '@shared/components/img-base64/img-base64.component';
import { AccessibilityPreferencesComponent } from '@shared/dialogs/accessibility-preferences/accessibility-preferences.component';
import { LocaleService, MsalAuthService, UserService } from '@shared/services';
import { CipoTheme, CipoThemeMode, TenantStore, UserStore } from '@signal-store';

@Component({
  selector: 'app-account-menu',
  templateUrl: './account-menu.component.html',
  styleUrls: ['./account-menu.component.scss'],
  imports: [
    MatMenuModule,
    MatIconModule,
    TranslateModule,
    CipoImgBase64,
    FlexLayoutModule,
    UserProfileHeaderComponent,
    OverlayModule,
    A11yModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountMenuComponent implements OnDestroy {
  readonly router = inject(Router);
  readonly userStore = inject(UserStore);
  readonly tenantStore = inject(TenantStore);
  readonly authService = inject(MsalAuthService);
  readonly isOpen = signal(false);

  lang = Language.English;

  private readonly _localeService = inject(LocaleService);
  private readonly _dialog = inject(MatDialog);
  private readonly _overlay = inject(Overlay);
  private readonly _injector = inject(Injector);
  private readonly _vcr = inject(ViewContainerRef);
  private readonly _userService = inject(UserService);

  private _overlayRef: OverlayRef = null;

  private readonly _triggerEl = viewChild.required('triggerBtn', { read: ElementRef });
  private readonly _overlayTpl = viewChild.required<TemplateRef<void>>('overlayTpl');
  // Hidden MatMenu whose instance satisfies `instanceof MatMenu` in MatMenuTriggerBase,
  // enabling the ◄ arrow and lateral submenu positioning on the Demo button.
  private readonly _panelCtxMenu = viewChild.required<MatMenu>('panelCtx');

  get languageType(): typeof Language {
    return Language;
  }

  get languageTypes(): number[] {
    return Object.keys(Language)
      .filter(l => !isNaN(Number(l)))
      .map(Number);
  }

  toggleMenu() {
    if (this.isOpen()) {
      this.closeMenu();
      return;
    }
    this._ensureOverlay();
    const injector = Injector.create({
      providers: [{ provide: MAT_MENU_PANEL, useValue: this._panelCtxMenu() }],
      parent: this._injector,
    });
    this._overlayRef!.attach(new TemplatePortal(this._overlayTpl(), this._vcr, undefined, injector));
    this.isOpen.set(true);

    // Tab inside any submenu emits panelCtx.closed('tab') via MatMenuTrigger cascade.
    // WAI-ARIA: Tab must close all menus and exit to the trigger.
    // Focus-restore for 'click' is handled per-button via (menuClosed) in the template.
    const triggerEl = this._triggerEl().nativeElement;
    this._panelCtxMenu()
      .closed.pipe(takeUntil(this._overlayRef!.detachments()))
      .subscribe(reason => {
        if (reason === 'tab') {
          this.closeMenu();
          setTimeout(() => triggerEl.focus());
        }
      });
  }

  private _ensureOverlay() {
    if (this._overlayRef) return;
    this._overlayRef = this._overlay.create({
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._triggerEl())
        .withPositions([
          { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
          { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
        ]),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });
    this._overlayRef.backdropClick().subscribe(() => this.closeMenu());
    this._overlayRef.detachments().subscribe(() => this.isOpen.set(false));
  }

  closeMenu() {
    if (this._overlayRef?.hasAttached()) {
      this._panelCtxMenu().closed.emit('click');
    }
    this.isOpen.set(false);
    this._overlayRef?.detach();
  }

  // Emitting panelCtx.closed causes MatMenuTrigger._menuClosingActions (parentClose stream)
  // to close any open Demo submenu without requiring mat-menu's ContentChildren hover tracking.
  closeDemo() {
    this._panelCtxMenu().closed.emit('click');
  }

  ngOnDestroy() {
    this._overlayRef?.dispose();
  }

  changeLanguage(language: Language) {
    this.lang = language;
    const langName = LanguageNames.get(this.lang);
    this._localeService.setLocale(langName);
    this.userStore.setLanguage(langName);
  }

  changeTheme(color: CipoTheme) {
    this.userStore.updateTheme({ color });
  }

  changeThemeMode(mode: CipoThemeMode) {
    this.userStore.updateTheme({ mode });
  }

  signOut() {
    this.closeMenu();
    this.authService.logout();
  }

  openPalette() {
    this._dialog.open(ColorPaletteComponent, { panelClass: ['cipo-dialog', 'classic'] });
    this.closeMenu();
  }

  openAccessibility() {
    this.closeMenu();
    this._dialog.open(AccessibilityPreferencesComponent, {
      panelClass: ['cipo-dialog', 'classic'],
      ...this._userService.getResponsiveDialogSize().md,
    });
  }

  goToProfile() {
    this.closeMenu();
    this.router.navigate([this.tenantStore.selectedTenantName(), 'my-profile']);
  }

  goToTenants() {
    this.closeMenu();
    this.tenantStore.selectTenant(null);
    this.router.navigate(['tenant-list']);
  }
}

